import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import {
  TbBuilding,
  TbChevronRight,
  TbClockHour4,
  TbLayoutDashboard,
  TbLock,
  TbLogout,
  TbMail,
  TbSwitchHorizontal,
  TbUser,
  TbWallet,
} from "react-icons/tb";
import { fetchAxios } from "../../../API/fetchAxios";
import {
  clearIbAccountPickPending,
  getStoredSelectedAccountId,
  setStoredSelectedAccountId,
} from "../../../Constants/accountTypes";
import { BAAS_BASE_PATH } from "../../../Pages/Private/BaasConsole/constants";
import {
  fetchOperatingAccounts,
  formatIbAccountPickerBankLine,
  formatIbAccountPickerEntityLine,
  formatIbAccountPickerPrimaryLine,
  formatIbAccountPickerSecondaryLine,
  mapOperatingAccountOption,
  resolveIbAccountDisplayType,
} from "../../../Services/operatingAccountsApi";
import {
  isInactiveOperatingAccount,
  resolveAccountLifecycle,
} from "../../../Utils/accountLifecycle";
import { isIbHomeSelectableAccount } from "../../../Utils/accountPixEligibility";
import { normalizeIbAccountId } from "../../../Utils/isSameIbAccount";

/** Same contact used on Conta Internacional Pix key samples. */
export const CARTEIRA_SUPPORT_MAILTO =
  "mailto:contato@valut.com.br?subject=Conta%20suspensa";

const IB_HOME_PATH = "/";

const accountOptionId = (account) => String(account?.id || account?.account_id || "");

const sameAccountId = (left, right) => {
  const a = normalizeIbAccountId(left);
  const b = normalizeIbAccountId(right);
  return Boolean(a) && Boolean(b) && a === b;
};

const hardRefreshCarteira = () => {
  const onHome =
    window.location.pathname === IB_HOME_PATH || window.location.pathname === "";
  if (onHome) {
    window.location.reload();
    return;
  }
  window.location.assign(IB_HOME_PATH);
};

const activateIbAccount = (accountId: string) =>
  fetchAxios({
    verb: "POST",
    context: "activateAccount",
    id: accountId,
    responseType: "json",
    cancelToken: undefined,
  });

const toPickerAccounts = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((row) => mapOperatingAccountOption(row?.raw && row?.id ? row.raw : row))
    .filter((account) => account.id && isIbHomeSelectableAccount(account.raw || account));
};

const firstNameFrom = (fullName: string) => {
  const first = String(fullName || "").trim().split(/\s+/)[0] || "";
  if (!first) return "";
  if (first.length === 1) return first.toUpperCase();
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};

const statusLabelForAccount = (account) => {
  if (!isInactiveOperatingAccount(account)) {
    return intl.get("carteira.gate.statusActive");
  }
  const lifecycle = resolveAccountLifecycle(account);
  if (lifecycle === "suspended") return intl.get("carteira.gate.statusSuspended");
  if (lifecycle === "deactivated") return intl.get("carteira.gate.statusDeactivated");
  return intl.get("carteira.gate.statusBlocked");
};

const statusToneForAccount = (account) => {
  if (!isInactiveOperatingAccount(account)) return "active";
  const lifecycle = resolveAccountLifecycle(account);
  if (lifecycle === "suspended") return "suspended";
  if (lifecycle === "deactivated") return "deactivated";
  return "blocked";
};

/** PF → user, PJ → building, VIRTUAL → wallet. */
const TypeIconForAccount = ({ account }) => {
  const type = resolveIbAccountDisplayType(account);
  const className = "carteira-gate__card-icon-svg";
  if (type === "PERSON") return <TbUser className={className} aria-hidden />;
  if (type === "VIRTUAL") return <TbWallet className={className} aria-hidden />;
  return <TbBuilding className={className} aria-hidden />;
};

type GateKind = "waiting" | "suspended" | "choose" | "loading";

type CarteiraAccessGateProps = {
  kind: GateKind;
  onLogout: () => void;
  initialAccounts?: unknown[];
  canAccessConsole?: boolean;
  userName?: string;
};

export const CarteiraAccessGate = ({
  kind,
  onLogout,
  initialAccounts = [],
  canAccessConsole = false,
  userName = "",
}: CarteiraAccessGateProps) => {
  const history = useHistory();
  const [accounts, setAccounts] = useState(() => toPickerAccounts(initialAccounts));
  const [accountsLoaded, setAccountsLoaded] = useState(
    Array.isArray(initialAccounts) && initialAccounts.length > 0,
  );
  const [showSwitcher, setShowSwitcher] = useState(kind === "choose");
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [inactivePickMessage, setInactivePickMessage] = useState(false);

  useEffect(() => {
    setShowSwitcher(kind === "choose");
  }, [kind]);

  useEffect(() => {
    if (kind !== "suspended" && kind !== "choose") return undefined;

    let cancelled = false;
    fetchOperatingAccounts()
      .then((loaded) => {
        if (cancelled) return;
        setAccounts(toPickerAccounts(loaded));
      })
      .catch(() => {
        if (cancelled) return;
        setAccounts((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : []));
      })
      .finally(() => {
        if (!cancelled) setAccountsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [kind]);

  const selectedId = getStoredSelectedAccountId();

  const hasOtherActiveAccounts = useMemo(() => {
    if (!accountsLoaded) return false;
    return accounts.some((account) => {
      if (sameAccountId(accountOptionId(account), selectedId)) return false;
      return !isInactiveOperatingAccount(account);
    });
  }, [accounts, accountsLoaded, selectedId]);

  const showPicker = kind === "choose" || (kind === "suspended" && showSwitcher);

  const greetingName = useMemo(() => {
    const first = firstNameFrom(userName);
    if (first) return first;
    if (String(userName || "").trim()) return "VALUT";
    return "";
  }, [userName]);

  const pickerTitle = greetingName
    ? intl.get("carteira.gate.chooseGreeting", { name: greetingName })
    : intl.get("carteira.gate.chooseTitle");

  const pickerSubtitle = useMemo(() => {
    if (!accountsLoaded) return intl.get("carteira.gate.chooseBody");
    const count = accounts.length;
    if (count <= 0) return intl.get("carteira.gate.noAccounts");
    if (count === 1) return intl.get("carteira.gate.chooseSubtitleOne");
    return intl.get("carteira.gate.chooseSubtitle", { count });
  }, [accounts.length, accountsLoaded]);

  const enterActiveAccount = useCallback(async (accountId: string) => {
    setSelectingId(accountId);
    setInactivePickMessage(false);
    // Persist first so PrivateApp re-evaluates against the chosen id.
    // Do not emit yet — a mid-flight /me GET used to unmount this gate.
    setStoredSelectedAccountId(accountId, { emitEvent: false });
    clearIbAccountPickPending();
    try {
      await activateIbAccount(accountId);
    } catch {
      // Selection stays client-side even if SetAppActive lags.
    }
    hardRefreshCarteira();
  }, []);

  const handlePickAccount = useCallback(
    (account) => {
      const nextId = accountOptionId(account);
      if (!nextId || selectingId) return;

      if (isInactiveOperatingAccount(account)) {
        setInactivePickMessage(true);
        return;
      }

      void enterActiveAccount(nextId);
    },
    [enterActiveAccount, selectingId],
  );

  const handleGoConsole = useCallback(() => {
    clearIbAccountPickPending();
    history.push(BAAS_BASE_PATH);
  }, [history]);

  const handleSupport = useCallback(() => {
    window.location.href = CARTEIRA_SUPPORT_MAILTO;
  }, []);

  const titleKey =
    kind === "loading"
      ? "carteira.gate.loadingTitle"
      : kind === "waiting"
        ? "carteira.gate.waitingTitle"
        : kind === "choose" || showPicker
          ? "carteira.gate.chooseTitle"
          : "carteira.gate.suspendedTitle";

  const bodyKey =
    kind === "loading"
      ? "carteira.gate.loadingBody"
      : kind === "waiting"
        ? "carteira.gate.waitingBody"
        : kind === "choose" || showPicker
          ? "carteira.gate.chooseBody"
          : hasOtherActiveAccounts
            ? "carteira.gate.suspendedBodyWithOthers"
            : "carteira.gate.suspendedBody";

  const showSwitchCta =
    kind === "suspended" && !showSwitcher;
  const showSupport = kind === "suspended" || kind === "choose";
  const hasActionsAboveLogout =
    showSwitchCta || canAccessConsole || showSupport;

  if (kind === "loading") {
    return (
      <div className="carteira-gate" data-testid="carteira-access-gate-loading">
        <div className="carteira-gate__panel">
          <header className="carteira-gate__header carteira-gate__header--simple">
            <span
              className="carteira-gate__status-icon carteira-gate__status-icon--waiting"
              aria-hidden
            >
              <TbClockHour4 className="carteira-gate__status-icon-svg" />
            </span>
            <h1 className="carteira-gate__title">
              {intl.get(titleKey) || "Carregando contas…"}
            </h1>
            <p className="carteira-gate__subtitle">
              {intl.get(bodyKey) || "Aguarde enquanto preparamos seu acesso."}
            </p>
          </header>
          <div className="carteira-gate__empty" role="status">
            {intl.get("carteira.gate.loadingAccounts") || "Carregando contas…"}
          </div>
          <div className="carteira-gate__actions">
            <button
              type="button"
              className="carteira-gate__btn-logout"
              onClick={() => onLogout()}
            >
              <TbLogout className="carteira-gate__action-icon" aria-hidden />
              {intl.get("carteira.gate.logout") || "Sair"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="carteira-gate">
      <div className="carteira-gate__panel">
        {showPicker ? (
          <header className="carteira-gate__header">
            <p className="carteira-gate__eyebrow">
              {intl.get("carteira.gate.chooseEyebrow")}
            </p>
            <h1 className="carteira-gate__title">{pickerTitle}</h1>
            <p className="carteira-gate__subtitle">{pickerSubtitle}</p>
          </header>
        ) : (
          <header className="carteira-gate__header carteira-gate__header--simple">
            <span
              className={`carteira-gate__status-icon carteira-gate__status-icon--${
                kind === "waiting" ? "waiting" : "suspended"
              }`}
              aria-hidden
            >
              {kind === "waiting" ? (
                <TbClockHour4 className="carteira-gate__status-icon-svg" />
              ) : (
                <TbLock className="carteira-gate__status-icon-svg" />
              )}
            </span>
            <h1 className="carteira-gate__title">{intl.get(titleKey)}</h1>
            <p className="carteira-gate__subtitle">
              {intl.get(bodyKey) || intl.get("carteira.gate.suspendedBody")}
            </p>
          </header>
        )}

        {showPicker ? (
          <div className="carteira-gate__list" role="list">
            {!accountsLoaded ? (
              <div className="carteira-gate__empty" role="status">
                {intl.get("carteira.gate.loadingAccounts")}
              </div>
            ) : accounts.length === 0 ? (
              <div className="carteira-gate__empty" role="status">
                {intl.get("carteira.gate.noAccounts")}
              </div>
            ) : (
              accounts.map((account) => {
                const id = accountOptionId(account);
                const inactive = isInactiveOperatingAccount(account);
                const entityName =
                  account.pickerEntityName
                  || account.entityName
                  || formatIbAccountPickerEntityLine(account);
                const numberLine =
                  account.pickerPrimary || formatIbAccountPickerPrimaryLine(account);
                const bankLine =
                  account.pickerBankLine || formatIbAccountPickerBankLine(account);
                const typeLine =
                  account.pickerSecondary || formatIbAccountPickerSecondaryLine(account);
                const isBusy = selectingId === id;
                const statusLabel = statusLabelForAccount(account);
                const statusTone = statusToneForAccount(account);
                const titleLine = entityName || numberLine;
                const showNumberUnderName =
                  Boolean(entityName)
                  && Boolean(numberLine)
                  && numberLine !== "—"
                  && numberLine !== entityName;
                return (
                  <button
                    key={id}
                    type="button"
                    role="listitem"
                    disabled={Boolean(selectingId) || inactive}
                    onClick={() => handlePickAccount(account)}
                    className={`carteira-gate__card${
                      inactive ? " carteira-gate__card--inactive" : ""
                    }${isBusy ? " carteira-gate__card--busy" : ""}`}
                  >
                    <span
                      className={`carteira-gate__card-icon carteira-gate__card-icon--${
                        resolveIbAccountDisplayType(account) || "business"
                      }`.toLowerCase()}
                      aria-hidden
                    >
                      <TypeIconForAccount account={account} />
                    </span>
                    <span className="carteira-gate__card-body">
                      <span className="carteira-gate__card-primary">
                        {isBusy
                          ? intl.get("carteira.gate.enteringAccount")
                          : titleLine}
                      </span>
                      {showNumberUnderName && !isBusy ? (
                        <span className="carteira-gate__card-number">{numberLine}</span>
                      ) : null}
                      {bankLine && !isBusy ? (
                        <span className="carteira-gate__card-bank">{bankLine}</span>
                      ) : null}
                      {typeLine && !isBusy ? (
                        <span className="carteira-gate__card-secondary">{typeLine}</span>
                      ) : null}
                      {inactive ? (
                        <span className="carteira-gate__card-hint">
                          {intl.get("carteira.gate.accountInactiveHint")}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`carteira-gate__pill carteira-gate__pill--${statusTone}`}
                    >
                      {statusLabel}
                    </span>
                    {!inactive ? (
                      <TbChevronRight
                        className="carteira-gate__chevron"
                        aria-hidden
                      />
                    ) : (
                      <span className="carteira-gate__chevron-spacer" aria-hidden />
                    )}
                  </button>
                );
              })
            )}
            {inactivePickMessage ? (
              <p className="carteira-gate__alert" role="alert">
                {intl.get("carteira.gate.inactivePickMessage")}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="carteira-gate__actions">
          {showSwitchCta ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setInactivePickMessage(false);
                setShowSwitcher(true);
              }}
            >
              <TbSwitchHorizontal
                className="carteira-gate__action-icon"
                aria-hidden
              />
              {intl.get("carteira.gate.switchAccount")}
            </button>
          ) : null}

          {canAccessConsole ? (
            <button
              type="button"
              className={showSwitchCta ? "btn-outline" : "btn-primary"}
              onClick={handleGoConsole}
            >
              <TbLayoutDashboard
                className="carteira-gate__action-icon"
                aria-hidden
              />
              {intl.get("carteira.gate.goToConsole")}
            </button>
          ) : null}

          {showSupport ? (
            <button
              type="button"
              className="btn-outline"
              onClick={handleSupport}
            >
              <TbMail className="carteira-gate__action-icon" aria-hidden />
              {intl.get("carteira.gate.contactSupport")}
            </button>
          ) : null}

          {hasActionsAboveLogout ? (
            <div className="carteira-gate__actions-divider" role="separator" />
          ) : null}

          <button
            type="button"
            className="carteira-gate__btn-logout"
            onClick={() => onLogout()}
          >
            <TbLogout className="carteira-gate__action-icon" aria-hidden />
            {intl.get("carteira.gate.logout")}
          </button>
        </div>
      </div>
    </div>
  );
};
